/**
 * ChatAvaSidebar — extracted from ConversationsChat.js (Session 3 refactor).
 * Jul 2026
 *
 * Purely presentational. Renders the left rail of AvA cards on the
 * `/conversations/practice/:avaSlug` chat page:
 *   • "Your AvAs" heading
 *   • Loading spinner while the AvA list is fetching
 *   • One card per AvA, with:
 *       - Circular avatar + "AI" badge
 *       - Real AvA name (Fix 3 — replaces the old anonymised
 *         "Female age 15" labels)
 *       - Subject/discipline subtitle (e.g. "Science & Technology Tutor",
 *         "Public Speaking Tutor", "Challenges Mentor")
 *       - Hover-visible trash button that opens the delete-conversation
 *         modal (Maya threads are protected — no trash button)
 *
 * Only the layout lives here; parent still owns selection state, AvA
 * fetch, and the delete-confirm modal.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { IdleLoopVideo } from './IdleLoopVideo';
import { apiClient } from '../../api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix 3 — subject/discipline titles per fixed AvA preset. Custom AvAs fall
// back to their personality type.
const PRESET_SUBTITLES = {
  maya_default: 'Practice Manager',
  rebecca_default: 'Science & Technology Tutor',
  neo_default: 'Challenges Mentor',
  specialist_leo: 'Relationships Tutor',
  specialist_marcus: 'Careers & Interviews Tutor',
  specialist_chloe: 'Friendships & Conflict Tutor',
  specialist_ethan: 'Public Speaking Tutor',
  specialist_sarah: 'Organisation & Time Tutor',
  specialist_petra: 'Digital Life Tutor',
  specialist_noah: 'Organisation & Time Tutor',
  specialist_sienna: 'Digital Life Tutor',
};

const NAME_SUBTITLES = {
  maya: 'Practice Manager',
  rebecca: 'Science & Technology Tutor',
  neo: 'Challenges Mentor',
  leo: 'Relationships Tutor',
  marcus: 'Careers & Interviews Tutor',
  chloe: 'Friendships & Conflict Tutor',
  ethan: 'Public Speaking Tutor',
  sarah: 'Organisation & Time Tutor',
  petra: 'Digital Life Tutor',
};

function labelFor(ava) {
  const name = (ava.name || '').trim();
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function subtitleFor(ava) {
  const preset = ava.avatar_config?.preset;
  if (preset && PRESET_SUBTITLES[preset]) return PRESET_SUBTITLES[preset];
  const byName = NAME_SUBTITLES[(ava.name || '').trim().toLowerCase()];
  if (byName) return byName;
  return ava.personality_type === 'Coach' ? 'Steady' : (ava.personality_type || 'Practice Tutor');
}

function isMayaAva(ava) {
  return (
    ava.avatar_config?.preset === 'maya_default'
    || (ava.name === 'Maya' && ava.is_primary)
  );
}

export const ChatAvaSidebar = ({
  avas,
  avasLoading,
  selectedAva,
  setSelectedAva,
  onDeleteRequest,
  lastSnippets,
}) => {
  // Oct 2026 — animated sidebar tiles: one batch call maps ava_id → idle
  // loop so every card plays its loop (static portrait until it exists).
  const [idleLoops, setIdleLoops] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.request('/api/ava-video/idle-loops-for-user');
        if (!cancelled && data?.idle_loops) setIdleLoops(data.idle_loops);
      } catch { /* static fallback */ }
    })();
    return () => { cancelled = true; };
  }, [avas?.length]);
  return (
    <div
      data-testid="chat-ava-sidebar"
      className="hidden lg:block w-64 xl:w-72 border-r border-gray-200 bg-gray-50 overflow-y-auto"
    >
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
          Your AvAs
        </h3>
        {avasLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-2">
            {avas.map((ava) => {
              const isActive = selectedAva?.ava_id === ava.ava_id;
              const snippet = lastSnippets?.[ava.ava_id]?.snippet;
              const cardClass = isActive
                ? 'bg-white shadow-md border-2 border-purple-500'
                : 'hover:bg-white hover:shadow-sm border-2 border-transparent';
              return (
                <div key={ava.ava_id} className={`relative group w-full rounded-xl transition-all ${cardClass}`}>
                  <button
                    onClick={() => setSelectedAva(ava)}
                    className="block w-full p-3 text-left"
                    data-testid={`ava-select-${ava.ava_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        {idleLoops[ava.ava_id] ? (
                          <div className="w-12 h-12 rounded-full overflow-hidden">
                            <IdleLoopVideo
                              src={`${API_URL}/api/files/${idleLoops[ava.ava_id]}`}
                              poster={ava.avatar_url}
                              className="w-full h-full object-cover"
                              testId={`sidebar-ava-video-${ava.ava_id}`}
                            />
                          </div>
                        ) : (
                          <img
                            src={ava.avatar_url}
                            alt={ava.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        )}
                        <span className="absolute -top-1 -right-1 px-1 py-0.5 bg-black/70 text-white text-[8px] font-bold rounded">AI</span>
                      </div>
                      <div className="text-left flex-1 pr-7 min-w-0">
                        <p
                          className="font-semibold text-gray-900"
                          data-testid={`sidebar-ava-label-${ava.ava_id}`}
                        >
                          {labelFor(ava)}
                        </p>
                        <p className="text-sm text-gray-500">{subtitleFor(ava)}</p>
                      </div>
                    </div>
                    {/* Fix 2 — last-conversation bubble: reminds the teen what
                        they last talked about without opening the thread. */}
                    {snippet && (
                      <div
                        className="mt-2 px-2.5 py-1.5 bg-gray-100 rounded-lg rounded-tl-none text-xs text-gray-600 line-clamp-2"
                        data-testid={`sidebar-last-snippet-${ava.ava_id}`}
                      >
                        {snippet}
                      </div>
                    )}
                  </button>
                  {/* Fix 2 — per-tutor delete icon: removes the tutor from the
                      active list by hard-deleting the conversation thread.
                      Maya (Practice Manager) stays protected — no button. */}
                  {!isMayaAva(ava) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteRequest(ava);
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-red-50 text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-100 transition-opacity"
                      title={`Remove ${ava.name} from your list`}
                      data-testid={`sidebar-delete-conv-${ava.ava_id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatAvaSidebar;
